import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, Skeleton, Typography, useTheme } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { selfRegistrationEnabled } from '../../../library/constants';
import { contentIcon } from '../../../library/contentIcons';
import { HomeCardContent, HomeContent } from '../../../library/models/homeContent';
import ResourcesIds from '../../../resources/ResourcesIds';
import { cardContentNoPaddingStyles, ctaButtonsContainerLargeStyles, ctaButtonsContainerStyles, ctaSubtitleStyles, ctaTextContainerStyles, ctaTitleStyles, featureAccentBarStyles, featureDescriptionStyles, featureIconBoxStyles, featureTitleStyles, featuresEyebrowStyles, featuresGridStyles, featuresHeaderContainerStyles, featuresHeadingStyles, featuresSectionStyles, featuresSubheadingStyles, getCtaSectionStyles, getFeatureCardStyles, getHeroSectionStyles, getHeroSectionWithImageStyles, getIconStyles, getPrimaryCtaButtonStyles, getPrimaryCtaLargeButtonStyles, getSecondaryCtaOutlinedWhiteStyles, getSignInButtonStyles, headlineAccentStyles, heroEyebrowStyles, heroLogoContainerStyles, heroSkeletonEyebrowStyles, heroSkeletonHeadlineStyles, heroSkeletonSubtitleStyles, mainHeadlineStyles, subtitleStyles } from './landingPage.styles';

export interface LandingPageViewProps {
    /** Every string the page draws, already resolved -- see `homeContent`. An empty one is not drawn. */
    content: HomeContent;
    /**
     * The clinic's page is still on its way: the hero's words are drawn as placeholders, the
     * rest of the page is held back, and the buttons are live. Nothing here is a fallback text,
     * because a clinic's page has none -- see `homeContent`.
     */
    isLoading?: boolean;
    /** Switches the primary button between Register and Dashboard, and hides Sign in. Register only while self-registration is on. */
    isAuthenticated: boolean;
    onPrimaryAction: () => void;
    onSignIn: () => void;
    onLearnMore: () => void;
}

/**
 * The front page, drawing content it is handed.
 *
 * Split from {@link LandingPage} when a clinic's administrator gained the ability to rewrite
 * the words on it. There are now two callers -- the page itself, and the live preview in the
 * console's Home tab -- and they must not be able to disagree about what the page looks like.
 * A preview built from a second copy of this markup is a preview that drifts, and it would
 * drift in the one direction that matters: an admin trusting it and publishing something else.
 *
 * <b>It decides nothing.</b> Which words, which language, and whether this is a clinic's page
 * or the platform's are all settled before it is called. The one thing it does with the words
 * is leave out an empty one -- no pill with nothing in it, no heading holding its margin open
 * over nothing -- because that is how a clinic takes a line off its page. That
 * is what lets the preview render an unsaved draft through exactly the code that will render
 * it to patients a moment later.
 *
 * The buttons are the exception, and they are handed in as callbacks rather than read from a
 * router: the preview draws them because they are part of the page's shape, and must not
 * navigate when an admin clicks one while editing.
 */
export const LandingPageView = ({
    content,
    isLoading = false,
    isAuthenticated,
    onPrimaryAction,
    onSignIn,
    onLearnMore,
}: LandingPageViewProps): JSX.Element => {
    const theme = useTheme();
    const { t: translate } = useTranslation();

    // Signed out, the primary button is Register, which is hidden -- see selfRegistrationEnabled.
    // Sign in stays beside it in the hero, and Learn more in the closing section.
    const showPrimaryAction: boolean = isAuthenticated || selfRegistrationEnabled;

    const hasHeadline: boolean = content.heroHeadline1 !== '' || content.heroHeadline2 !== '';
    const hasFeatures: boolean = content.featuresEyebrow !== '' || content.featuresTitle !== ''
        || content.featuresSubtitle !== '' || content.cards.length > 0;
    const hasFeaturesHeader: boolean = content.featuresEyebrow !== '' || content.featuresTitle !== ''
        || content.featuresSubtitle !== '';

    return (
        <Box>
            {/* Hero Section */}
            <Box sx={content.heroImageUrl
                ? getHeroSectionWithImageStyles(theme, content.heroImageUrl)
                : getHeroSectionStyles(theme)}
            >
                <Container maxWidth="lg">
                    <Box sx={heroLogoContainerStyles}>
                        {isLoading ? (
                            <Box data-testid="home-loading">
                                <Skeleton variant="rounded" width={160} height={30} sx={heroSkeletonEyebrowStyles} />
                                <Skeleton variant="text" width={520} sx={heroSkeletonHeadlineStyles} />
                                <Skeleton variant="text" width={420} sx={heroSkeletonHeadlineStyles} />
                                <Skeleton variant="text" width={600} height={32} sx={heroSkeletonSubtitleStyles} />
                            </Box>
                        ) : (
                            <>
                                {/* Eyebrow */}
                                {content.heroEyebrow && (
                                    <Box sx={heroEyebrowStyles}>{content.heroEyebrow}</Box>
                                )}

                                {/* Main Headline */}
                                {hasHeadline && (
                                    <Typography
                                        variant="h1"
                                        component="h1"
                                        gutterBottom
                                        sx={mainHeadlineStyles(theme)}
                                    >
                                        {content.heroHeadline1}
                                        {content.heroHeadline1 && content.heroHeadline2 && <br />}
                                        {content.heroHeadline2 && (
                                            <Box component="span" sx={headlineAccentStyles(theme)}>
                                                {content.heroHeadline2}
                                            </Box>
                                        )}
                                    </Typography>
                                )}

                                {/* Subtitle */}
                                {content.heroSubtitle && (
                                    <Typography
                                        variant="h5"
                                        component="p"
                                        sx={subtitleStyles(theme)}
                                    >
                                        {content.heroSubtitle}
                                    </Typography>
                                )}
                            </>
                        )}

                        {/* CTA Buttons */}
                        <Box sx={ctaButtonsContainerStyles}>
                            {showPrimaryAction && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={onPrimaryAction}
                                    endIcon={<ArrowForward />}
                                    sx={getPrimaryCtaButtonStyles(theme)}
                                >
                                    {/* Points at the dashboard, not the booking wizard, until the
                                        appointments rebuild restores /appointments. */}
                                    {isAuthenticated ? translate(ResourcesIds.DASHBOARD) : translate(ResourcesIds.REGISTER)}
                                </Button>
                            )}
                            {!isAuthenticated && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    onClick={onSignIn}
                                    sx={getSignInButtonStyles(theme)}
                                >
                                    {translate(ResourcesIds.LOGIN)}
                                </Button>
                            )}
                        </Box>

                    </Box>
                </Container>
            </Box>

            {/* Features Section -- left out whole when a clinic has taken every part of it off */}
            {!isLoading && hasFeatures && (
                <Container maxWidth="lg" sx={featuresSectionStyles}>
                    {hasFeaturesHeader && (
                        <Box sx={featuresHeaderContainerStyles}>
                            {content.featuresEyebrow && (
                                <Box sx={featuresEyebrowStyles}>{content.featuresEyebrow}</Box>
                            )}
                            {content.featuresTitle && (
                                <Typography
                                    variant="h2"
                                    component="h2"
                                    gutterBottom
                                    sx={featuresHeadingStyles(theme)}
                                >
                                    {content.featuresTitle}
                                </Typography>
                            )}
                            {content.featuresSubtitle && (
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={featuresSubheadingStyles}
                                >
                                    {content.featuresSubtitle}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={featuresGridStyles}>
                        {content.cards.map((card: HomeCardContent) => (
                            <Card key={card.key} sx={getFeatureCardStyles(theme)}>
                                <Box sx={featureAccentBarStyles} />
                                <CardContent sx={cardContentNoPaddingStyles}>
                                    <Box sx={featureIconBoxStyles}>{contentIcon(card.icon, getIconStyles(theme))}</Box>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        gutterBottom
                                        sx={featureTitleStyles(theme)}
                                    >
                                        {card.title}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={featureDescriptionStyles}
                                    >
                                        {card.body}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Container>
            )}

            {/* Closing CTA */}
            <Box sx={getCtaSectionStyles(theme)}>
                <Container maxWidth="lg">
                    <Box sx={ctaTextContainerStyles}>
                        {!isLoading && content.ctaTitle && (
                            <Typography variant="h2" component="h2" gutterBottom sx={ctaTitleStyles}>
                                {content.ctaTitle}
                            </Typography>
                        )}
                        {!isLoading && content.ctaSubtitle && (
                            <Typography variant="h6" sx={ctaSubtitleStyles(theme)}>
                                {content.ctaSubtitle}
                            </Typography>
                        )}
                        <Box sx={ctaButtonsContainerLargeStyles}>
                            {showPrimaryAction && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    onClick={onPrimaryAction}
                                    endIcon={<ArrowForward />}
                                    sx={getPrimaryCtaLargeButtonStyles(theme)}
                                >
                                    {/* Points at the dashboard, not the booking wizard, until the
                                        appointments rebuild restores /appointments. */}
                                    {isAuthenticated ? translate(ResourcesIds.DASHBOARD) : translate(ResourcesIds.REGISTER)}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={onLearnMore}
                                sx={getSecondaryCtaOutlinedWhiteStyles(theme)}
                            >
                                {translate(ResourcesIds.LEARN_MORE)}
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};
